import React, { useMemo } from 'react';
import { SystemCard } from '../components/SystemCard';
import { SystemCardPopoverContent } from '../components/SystemCardPopoverContent';
import { WaterPumpWithMetrics } from '../types';
import { iconForEnergySink, isPassiveTechnology } from '../utils';

const WATER_PUMP_SYSTEM_IMAGE = (
  <svg
    width='61'
    height='61'
    viewBox='0 0 61 61'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M8.4469 14.3597H17.2979H19.2361C19.9667 14.3597 20.5663 13.5492 20.5663 12.549V8.10533H30.8911C32.9602 8.10533 34.6451 10.399 34.6451 13.2158V17.464H31.2766V21.8621H33.6212H37.4124H39.5843V17.464H35.6333V13.2158C35.6333 9.65169 33.505 6.75442 30.8868 6.75442H20.5621V2.31079C20.5621 1.31629 19.9667 0.5 19.2319 0.5H6.94359C5.82877 0.5 4.92086 1.73594 4.92086 3.25355V11.5947C4.92086 13.1123 5.82877 14.3482 6.94359 14.3482H8.4469V14.3597ZM16.9689 21.8621V17.464H8.66121V21.8621H11.0058H14.797H16.9689ZM22.5074 53.8996V25.0036H16.4908H12.1084H6.41168C6.12185 25.0036 5.89198 25.2722 5.89198 25.5979V35.3918V43.5115V53.3054C5.89198 53.6368 6.12684 53.8996 6.41168 53.8996H9.41494H18.9894H22.5074ZM9.50489 51.5683C8.89024 51.5683 8.39553 51.0026 8.39553 50.2998C8.39553 49.5969 8.89024 49.0313 9.50489 49.0313C10.1195 49.0313 10.6142 49.5969 10.6142 50.2998C10.6142 51.0026 10.1145 51.5683 9.50489 51.5683ZM9.50489 29.8834C8.89024 29.8834 8.39553 29.3177 8.39553 28.6149C8.39553 27.9121 8.89024 27.3464 9.50489 27.3464C10.1195 27.3464 10.6142 27.9121 10.6142 28.6149C10.6142 29.312 10.1145 29.8834 9.50489 29.8834ZM18.8895 27.3464C19.5041 27.3464 19.9988 27.9121 19.9988 28.6149C19.9988 29.3177 19.5041 29.8834 18.8895 29.8834C18.2748 29.8834 17.7801 29.3177 17.7801 28.6149C17.7801 27.9121 18.2798 27.3464 18.8895 27.3464ZM17.7801 50.2998C17.7801 49.5969 18.2748 49.0313 18.8895 49.0313C19.5041 49.0313 19.9988 49.5969 19.9988 50.2998C19.9988 51.0026 19.5041 51.5683 18.8895 51.5683C18.2798 51.5683 17.7801 50.9969 17.7801 50.2998ZM0.353516 44.5036V33.7659C0.353516 33.0879 0.547046 32.5432 0.791921 32.5432H3.58428V45.7375H0.791921C0.550995 45.7375 0.353516 45.1817 0.353516 44.5036ZM58.0099 24.7508H56.2827V53.8269H58.0099C59.302 53.8269 60.3535 52.3955 60.3535 50.6365V27.947C60.3535 26.1879 59.302 24.7508 58.0099 24.7508ZM50.7889 56.4185H53.9644C54.6992 56.4185 55.2946 57.2291 55.2904 58.2293V60.5H5.71475V58.2293C5.71475 57.2291 6.31439 56.4185 7.04493 56.4185H8.69605H16.7869H33.4416H50.7889ZM27.5618 27.7803H24.9817V50.8032H27.5618V27.7803ZM29.4304 25.0036H53.892V53.9053H29.4304V25.0036ZM49.0521 43.8077C49.0521 47.6495 44.9508 49.953 41.892 49.953C38.8332 49.953 34.7319 47.6495 34.7319 43.8077C34.7319 40.075 39.4034 33.8515 41.6933 29.179L41.892 28.7734C43.5074 31.9149 49.0521 39.9659 49.0521 43.8077Z'
      fill='currentColor'
    />
  </svg>
);

type WaterPumpUnitProps = {
  waterPump: WaterPumpWithMetrics;
  selected: boolean;
  count?: number;
  isLoading?: boolean;
  readOnly?: boolean;
  actions: {
    onAdd?: () => void;
    onIncrement?: () => void;
    onDecrement?: () => void;
    onRemove?: () => void;
    onChangeCount?: (newCount: number) => void;
  };
};

export const WaterPumpUnit: React.FC<WaterPumpUnitProps> = React.memo(
  ({ waterPump, selected, count, isLoading, actions, readOnly }) => {
    const EnergyIcon = iconForEnergySink(waterPump.energySink);

    const icons = useMemo(
      () => ({
        EnergyIcon: EnergyIcon,
        energyIconCount: waterPump.relativeEnergyDemand,
        costIconCount: waterPump.relativeCost,
      }),
      [EnergyIcon, waterPump.relativeEnergyDemand, waterPump.relativeCost],
    );

    const hoverPopover = useMemo(
      () => (
        <SystemCardPopoverContent
          name={waterPump.name}
          energySource={waterPump.energySink}
          EnergySourceIcon={EnergyIcon}
          limit={waterPump.limit}
          coveragePercentage={waterPump.coveragePercentage}
          energyDemand={Number(waterPump.energyUsePerYr)}
          capitalCost={Number(waterPump.capitalCostPerUnit)}
          annualCost={Number(waterPump.maintenanceCostPerUnit)}
          coverageFieldTitle={'water pump'}
          designLife={waterPump.designLife}
          isLoading={isLoading}
        />
      ),
      [waterPump, EnergyIcon, isLoading],
    );

    return (
      <SystemCard
        actions={actions}
        count={count}
        limit={waterPump.limit}
        name={waterPump.name}
        selected={selected}
        systemImage={WATER_PUMP_SYSTEM_IMAGE}
        icons={icons}
        hoverPopover={hoverPopover}
        readOnly={readOnly}
        unitsFixed={isPassiveTechnology(waterPump)}
      />
    );
  },
);

WaterPumpUnit.displayName = 'WaterPumpUnit';
